/**
 * Compress an image file using HTML5 canvas.
 * Scales down maintaining aspect ratio (max 600px default).
 * Tries WebP encoding first, falls back to JPEG if unsupported.
 *
 * @param {File} file - Original file object
 * @param {number} maxWidth - Max allowed width (default 600)
 * @param {number} maxHeight - Max allowed height (default 600)
 * @param {number} quality - Compression quality 0-1 (default 0.8)
 * @returns {Promise<File>} Compressed File object
 */
export function compressImage(file, maxWidth = 600, maxHeight = 600, quality = 0.8) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      resolve(file)
      return
    }

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = (event) => {
      const img = new Image()
      img.src = event.target.result
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let width = img.width
        let height = img.height

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height
            height = maxHeight
          }
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        const baseName = file.name ? file.name.replace(/\.[^/.]+$/, '') : 'student_photo'

        // Try WebP first
        canvas.toBlob(
          (webpBlob) => {
            if (webpBlob && webpBlob.type === 'image/webp') {
              const compressedFile = new File([webpBlob], baseName + '.webp', {
                type: 'image/webp',
                lastModified: Date.now(),
              })
              if (file.uid) compressedFile.uid = file.uid
              resolve(compressedFile)
            } else {
              // Fallback to JPEG
              canvas.toBlob(
                (jpegBlob) => {
                  if (jpegBlob) {
                    const compressedFile = new File([jpegBlob], baseName + '.jpg', {
                      type: 'image/jpeg',
                      lastModified: Date.now(),
                    })
                    if (file.uid) compressedFile.uid = file.uid
                    resolve(compressedFile)
                  } else {
                    resolve(file)
                  }
                },
                'image/jpeg',
                quality
              )
            }
          },
          'image/webp',
          quality
        )
      }
      img.onerror = () => resolve(file)
    }
    reader.onerror = () => resolve(file)
  })
}
